import React, { useRef, useState } from 'react';
import { Edit3, Play } from 'lucide-react';
import { IActivityDisplayState } from '../../Models/trips/trip-activity.model';
import { TripImageSlideshow } from '../trips/trip-image-slideshow';
import YouTubePlayer from '../youtube/youtube-player';
import { useVideoPlayerOptional } from '../contexts/video-player-context';
import { AuthenticatedImage } from '../UI/authenticated-image';
import styles from './activity-image-slideshow.module.scss';

// Remember tokens explicitly paused by users so remounts do not auto-restart.
const pausedAutoPlayTokens = new Set<string>();

export interface ActivityImageSlideshowProps {
  /** Resolved display state from the activity. When absent, a fallback image is shown. */
  displayState?: IActivityDisplayState;
  /** YouTube video ID — required only when displayMode is 'video_clip'. */
  videoId?: string;
  /** Alt text for images. */
  altText: string;
  /** Fallback image URL used when displayState is absent or has no images. */
  fallbackImageUrl?: string;
  /** Optional token; when changed, opens inline video playback. */
  autoPlayToken?: string;
  /** Global playback session token shared by all cards. */
  playbackSessionToken?: string;
  /** Whether this card is the active owner for the current playback session. */
  playbackOwner?: boolean;
  /** When provided an edit-pencil icon appears on hover. */
  onEditClick?: () => void;
}

/**
 * Renders the image/video area inside an activity card based on `displayState`.
 *
 * Modes:
 *  - `slideshow`   — auto-advancing image strip; skeleton while images are resolving
 *  - `video_clip`  — static thumbnail at rest; YouTube player on hover (muted, auto-play)
 *  - `image`       — single static image
 *  - absent state  — falls back to `fallbackImageUrl` or skeleton
 */
export function ActivityImageSlideshow({
  displayState,
  videoId,
  altText,
  fallbackImageUrl,
  autoPlayToken,
  playbackSessionToken,
  playbackOwner = false,
  onEditClick
}: ActivityImageSlideshowProps) {
  const [showInlineVideo, setShowInlineVideo] = useState(false);
  const playerRef = useRef<any>(null);
  const activeVideoIdRef = useRef<string | undefined>(undefined);
  const videoPlayerCtx = useVideoPlayerOptional();
  const displayMode = displayState?.displayMode;

  React.useEffect(() => {
    if (
      displayMode === 'video_clip' &&
      !!videoId &&
      autoPlayToken &&
      playbackOwner
    ) {
      if (pausedAutoPlayTokens.has(autoPlayToken)) {
        console.log(
          '[ActivityImageSlideshow] Skipping auto-play for paused token',
          {
            videoId,
            autoPlayToken
          }
        );
        return;
      }

      console.log('[ActivityImageSlideshow] Auto-play token received', {
        videoId,
        autoPlayToken,
        playbackOwner
      });

      if (
        showInlineVideo &&
        playerRef.current &&
        activeVideoIdRef.current === videoId
      ) {
        const targetStart = displayState?.videoStartTime ?? 0;
        try {
          playerRef.current.seekTo(targetStart, true);
          playerRef.current.playVideo?.();
          console.log('[ActivityImageSlideshow] Seeked existing player', {
            videoId,
            targetStart
          });
          return;
        } catch (error) {
          console.warn(
            '[ActivityImageSlideshow] Seek failed, reopening player',
            {
              videoId,
              error
            }
          );
        }
      }

      setShowInlineVideo(true);
    }
  }, [
    autoPlayToken,
    displayMode,
    displayState?.videoStartTime,
    playbackOwner,
    showInlineVideo,
    videoId
  ]);

  React.useEffect(() => {
    if (!playbackSessionToken || playbackOwner) return;
    if (!showInlineVideo) return;

    try {
      playerRef.current?.pauseVideo?.();
    } catch (error) {
      console.warn('[ActivityImageSlideshow] Failed to pause non-owner video', {
        error
      });
    }
    setShowInlineVideo(false);
  }, [playbackSessionToken, playbackOwner, showInlineVideo]);

  React.useEffect(() => {
    if (!displayState) return;
    console.log('[ActivityImageSlideshow] Render state', {
      displayMode: displayState.displayMode,
      hasVideoId: !!videoId,
      videoId,
      showInlineVideo,
      autoPlayToken,
      displayImagesCount: displayState.displayImages?.length ?? 0,
      videoStartTime: displayState.videoStartTime,
      videoEndTime: displayState.videoEndTime
    });
  }, [displayState, videoId, showInlineVideo, autoPlayToken]);

  function handlePlayerReady(player: any) {
    playerRef.current = player;
    activeVideoIdRef.current = videoId;
    console.log('[ActivityImageSlideshow] Player ready', {
      videoId,
      showInlineVideo
    });
    videoPlayerCtx?.registerPlayer(player);
  }

  function renderEditButton() {
    if (!onEditClick) return null;
    return (
      <button
        type='button'
        className={styles.editButton}
        title='Edit display'
        onClick={(e) => {
          e.stopPropagation();
          onEditClick();
        }}
      >
        <Edit3 size={14} />
      </button>
    );
  }

  function renderNowPlayingBadge() {
    if (!(playbackOwner && showInlineVideo && displayMode === 'video_clip')) {
      return null;
    }

    return <div className={styles.nowPlayingBadge}>Now Playing</div>;
  }

  // ── No displayState: use fallback image or skeleton ──────────────────────
  if (!displayState) {
    return (
      <div className={styles.container}>
        {fallbackImageUrl ? (
          <AuthenticatedImage
            src={fallbackImageUrl}
            alt={altText}
            className={styles.singleImage}
            fallback={<div className={styles.skeleton} />}
          />
        ) : (
          <div className={styles.skeleton} />
        )}
        {renderEditButton()}
      </div>
    );
  }

  const { displayImages, imagesResolved, videoStartTime, videoEndTime } =
    displayState;

  // ── video_clip mode ───────────────────────────────────────────────────────
  if (displayMode === 'video_clip') {
    const thumbnailUrl = displayImages[0] ?? fallbackImageUrl;
    const showPlayer = showInlineVideo && !!videoId;

    return (
      <div
        className={styles.container}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Static thumbnail — always rendered so layout is stable */}
        {thumbnailUrl ? (
          <AuthenticatedImage
            src={thumbnailUrl}
            alt={altText}
            className={styles.thumbnail}
            style={{ opacity: showPlayer ? 0 : 1, transition: 'opacity 0.2s' }}
            fallback={<div className={styles.skeleton} />}
          />
        ) : (
          <div className={styles.skeleton} />
        )}

        {!showPlayer && !!videoId && (
          <button
            type='button'
            className={styles.videoPlayButton}
            aria-label='Play video in card'
            onClick={(e) => {
              e.stopPropagation();
              setShowInlineVideo(true);
            }}
          >
            <Play size={20} />
          </button>
        )}

        {/* YouTube player — mounted only when explicitly opened in-card */}
        {showPlayer && (
          <div className={styles.videoOverlay}>
            <YouTubePlayer
              videoId={videoId!}
              autoplay
              controls={true}
              startSeconds={videoStartTime ?? 0}
              endSeconds={videoEndTime}
              width='100%'
              height='100%'
              onReady={handlePlayerReady}
              onPause={() => {
                if (autoPlayToken) {
                  pausedAutoPlayTokens.add(autoPlayToken);
                }
              }}
              onPlay={() => {
                if (autoPlayToken) {
                  pausedAutoPlayTokens.delete(autoPlayToken);
                }
              }}
            />
          </div>
        )}

        {renderNowPlayingBadge()}
        {renderEditButton()}
      </div>
    );
  }

  // ── slideshow mode ────────────────────────────────────────────────────────
  if (displayMode === 'slideshow') {
    const hasImages = imagesResolved && displayImages.length > 0;

    return (
      <div className={styles.container}>
        {hasImages ? (
          <TripImageSlideshow
            images={displayImages}
            altText={altText}
          />
        ) : (
          <div className={styles.skeleton} />
        )}
        {renderEditButton()}
      </div>
    );
  }

  // ── image mode (and any unrecognised mode) ────────────────────────────────
  const imageUrl = displayImages[0] ?? fallbackImageUrl;

  return (
    <div className={styles.container}>
      {imageUrl ? (
        <AuthenticatedImage
          src={imageUrl}
          alt={altText}
          className={styles.singleImage}
          fallback={<div className={styles.skeleton} />}
        />
      ) : (
        <div className={styles.skeleton} />
      )}
      {renderEditButton()}
    </div>
  );
}
